import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Second login step for MFA-enrolled Owner/Ops Admin. mode="verify": enter a
// TOTP or backup code. mode="enroll": after an Owner-forced reset, scan a new
// QR + confirm before any session is issued.
export default function MfaChallenge({ mfaToken, mode = 'verify', onSuccess, onCancel }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [pendingLogin, setPendingLogin] = useState(null);
  const authHeaders = { headers: { Authorization: `Bearer ${mfaToken}` } };

  useEffect(() => {
    if (mode !== 'enroll') return;
    axios.post(`${API}/auth/mfa/setup`, {}, authHeaders).then((r) => setSetup(r.data))
      .catch(() => toast.error('Could not start MFA enrollment — log in again'));
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      if (mode === 'enroll') {
        const { data } = await axios.post(`${API}/auth/mfa/confirm`, { code }, authHeaders);
        setBackupCodes(data.backup_codes);
        setPendingLogin({ user: data.user, token: data.token });
      } else {
        const { data } = await axios.post(`${API}/auth/mfa/verify`, { mfa_token: mfaToken, code });
        if (data.backup_code_used) toast.warning('Backup code used — it will not work again');
        onSuccess(data.user, data.token);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally { setBusy(false); }
  };

  if (backupCodes) {
    return (
      <div className="space-y-4" data-testid="mfa-backup-codes">
        <p className="text-sm font-medium">MFA enrolled. Save these one-time backup codes now — they are shown once.</p>
        <div className="grid grid-cols-2 gap-1 font-mono text-sm bg-muted p-3 rounded" data-testid="mfa-backup-codes-list">
          {backupCodes.map((c) => <span key={c}>{c}</span>)}
        </div>
        <Button className="w-full" data-testid="mfa-enroll-continue-btn" onClick={() => onSuccess(pendingLogin.user, pendingLogin.token)}>
          I saved them — continue
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="mfa-challenge-form">
      <div className="flex items-center gap-2 text-sm font-medium"><ShieldCheck className="h-4 w-4" />
        {mode === 'enroll' ? 'Your MFA was reset — enroll a new authenticator to continue' : 'Two-factor authentication'}
      </div>
      {mode === 'enroll' && (setup ? (
        <div className="space-y-2">
          <img src={setup.qr_code} alt="MFA QR code" className="h-40 w-40 mx-auto border rounded" data-testid="mfa-enroll-qr" />
          <p className="text-xs text-muted-foreground break-all">Manual key: <span className="font-mono" data-testid="mfa-enroll-secret">{setup.secret}</span></p>
        </div>
      ) : <p className="text-xs text-muted-foreground">Generating QR code…</p>)}
      <div>
        <Label htmlFor="mfa-code">{mode === 'enroll' ? 'Code from your new authenticator' : 'Authenticator code or backup code'}</Label>
        <Input id="mfa-code" data-testid="mfa-code-input" value={code} autoFocus autoComplete="one-time-code"
               onChange={(e) => setCode(e.target.value)} placeholder="123456" />
      </div>
      <div className="flex gap-2">
        <Button type="submit" className="flex-1" disabled={busy} data-testid="mfa-submit-btn">{mode === 'enroll' ? 'Enroll & sign in' : 'Verify'}</Button>
        {onCancel && <Button type="button" variant="outline" onClick={onCancel} data-testid="mfa-cancel-btn">Cancel</Button>}
      </div>
    </form>
  );
}
